// React Lib
import React, { useState } from "react";
import classnames from 'classnames';
// Component Styles
import { useStyles } from "./SystemStyles";
// Material UI Core
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableSortLabel from '@mui/material/TableSortLabel';
import Grid from '@mui/material/Grid';
// @otg-one/ui_components/components
import { Button, Chip, Typography, Tooltip, OverflowMenu } from "../index";
// @otg-one/ui_components/icons
import { CPU, Device, Bug, EmptySystem } from "../../icons";
// @otg-one/ui_components/constants
import { Color, FONT_FAMILY } from "../../constants";
import Switch from "../Switch";
import { getFunctionIcon } from "../../utils/Utils";
import { ArrowUp, Delete, Database, ShowMoreHorizontal, ShowMoreVertical } from "../../sap_icons";
const DeviceCount = (props) => {
    const classes = useStyles(props);
    const { data, activeDevice, deviceLimit } = props;
    return (React.createElement("div", { className: classes.systemDeviceCountContainer },
        React.createElement("div", { className: classes.systemDeviceCount },
            React.createElement(Tooltip, { title: "Device Count", placement: "right" },
                React.createElement("div", { style: { marginTop: 2 } },
                    React.createElement(Device, { style: { color: Color.primary[400] } }))),
            data.deploymentEnv === 0 ? (React.createElement(React.Fragment, null,
                React.createElement(Typography, { fontFamily: FONT_FAMILY.ROBOTO_BOLD, size: 48, color: Color.neutral[400], style: { letterSpacing: -1.7, marginLeft: 5 } }, deviceLimit),
                React.createElement(Typography, { fontFamily: FONT_FAMILY.ROBOTO_BOLD, size: 18, color: Color.neutral[600], style: { marginLeft: 10, marginTop: -5 } }, `Parallel Device Max`))) : (React.createElement(React.Fragment, null,
                React.createElement(Typography, { fontFamily: FONT_FAMILY.ROBOTO_BOLD, size: 48, color: Color.neutral[400], style: { letterSpacing: -1.7, marginLeft: 5 } }, activeDevice),
                React.createElement(Typography, { fontFamily: FONT_FAMILY.ROBOTO_BOLD, size: 18, color: Color.neutral[600], style: { marginLeft: 10, marginTop: -25, width: 50 } }, `/ ${deviceLimit}`),
                React.createElement("div", { style: { width: "100%", height: 5, background: Color.neutral[200] } },
                    React.createElement("div", { style: { width: `${(activeDevice / deviceLimit) * 100}%`, height: 5, background: Color.gradient[100] } })))))));
};
const ApplicationFunction = (props) => {
    const classes = useStyles(props);
    const { data } = props;
    return (React.createElement(React.Fragment, null,
        React.createElement(Typography, { fontFamily: FONT_FAMILY.CAPITOLIUM, size: 16, color: Color.neutral[900], style: { marginTop: 35 } }, "Application Functions"),
        React.createElement("div", { className: classes.systemApplicationFunctionIconListContainer, style: { marginTop: 5 } }, data.functions.map((func, index) => {
            const isActive = func.isActive === 1;
            const functionClasses = classnames({
                [classes.activeFunction]: isActive,
                [classes.inactiveFunction]: !isActive
            });
            return (React.createElement("div", { className: classes.systemApplicationFunction, style: {
                    color: Color.primary[500],
                    marginLeft: index > 0 && 20
                } },
                React.createElement(Tooltip, { title: func.name, placement: "right" },
                    React.createElement("div", { className: functionClasses }, getFunctionIcon(func.functionTypeId))),
                React.createElement(Tooltip, { title: "Customized", placement: "right" },
                    React.createElement("div", { className: classes.systemApplicationFunctionIconStatus, style: {
                            display: func.isChanged ? "block" : "none",
                            color: Color.neutral[0],
                        } }))));
        }))));
};
const desc = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
};
const stableSort = (array, cmp) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0)
            return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
};
const getSorting = (order, orderBy) => {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
};
const headCells = [
    { id: 'appName', left: true, sortable: false, label: `App. Name` },
    { id: 'status', left: true, sortable: false, label: `Status - PIN No.` },
    { id: 'dbName', left: true, sortable: false, label: `DB Name` },
    { id: 'systemName', left: true, sortable: false, label: `Syst Name` },
    { id: 'device', left: false, sortable: false, label: `Device` },
    { id: 'appFunctions', left: true, sortable: false, label: `App Function` },
    { id: 'debug', left: true, sortable: false, label: `Debug Mode` },
    { id: 'moreAction', left: false, sortable: false, label: `` }
];
const CustomTableHead = (props) => {
    const classes = useStyles(props);
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };
    return (React.createElement(TableHead, null,
        React.createElement(TableRow, { style: { borderBottom: `2px solid ${Color.neutral[300]}` } }, headCells.map(headCell => (React.createElement(TableCell, { key: headCell.id, align: headCell.left ? 'left' : 'right', sortDirection: orderBy === headCell.id ? order : false, style: { fontFamily: FONT_FAMILY.ROBOTO_MEDIUM, color: Color.neutral[600] } }, headCell.sortable ? (React.createElement(TableSortLabel, { active: orderBy === headCell.id, direction: orderBy === headCell.id ? order : 'asc', onClick: headCell.sortable && createSortHandler(headCell.id), IconComponent: ArrowUp },
            headCell.label,
            orderBy === headCell.id ? (React.createElement("span", { className: classes.visuallyHidden }, order === 'desc' ? 'sorted descending' : 'sorted ascending')) : null)) : (React.createElement(React.Fragment, null, headCell.label))))))));
};
const SystemListHeader = (props) => {
    const classes = useStyles(props);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('cost');
    const { id, data, children } = props;
    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    return (React.createElement("div", { className: classes.root },
        React.createElement(TableContainer, null,
            React.createElement(Table, { className: classes.table },
                React.createElement(CustomTableHead, { classes: classes, order: order, orderBy: orderBy, onRequestSort: handleRequestSort, rowCount: data.length }),
                React.createElement(TableBody, null, children)))));
};
const SystemView = (props) => {
    const classes = useStyles(props);
    const { id, systemView, data, onClick, setActiveSystem, setDeleteSystemDialogOpen, handleEditSystemConfiguration, setCopyFunctionConfigurationDialogOpen, setEditSystemDeviceLimitDialogOpen } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const [isSystemNameHovered, setIsSystemNameHovered] = useState(null);
    const [isDebugMode, setIsDebugMode] = useState(false);
    const deviceLimit = data.deploymentEnv === 0 || data.deviceLimit == null ? data.defaultDeviceLimit : data.deviceLimit;
    const activeDevice = data.activeDevice > deviceLimit ? deviceLimit : data.activeDevice;
    const handleChangeIsDebugMode = () => {
        setIsDebugMode(prev => !prev);
    };
    const handleClickTableCell = (event) => {
        event.stopPropagation();
        onClick();
    };
    const stopClick = (e) => {
        e.stopPropagation();
    };
    const handleSystemNameOnMouseOver = () => {
        setIsSystemNameHovered(true);
    };
    const handleSystemNameOnMouseOut = () => {
        setIsSystemNameHovered(false);
    };
    const handleClickShowMoreIcon = (event) => {
        event.stopPropagation();
        if (!anchorEl) {
            setAnchorEl(event.currentTarget);
        }
        ;
    };
    const menuItems = [
        {
            text: "Go to System dashboard",
            onClick: () => {
                onClick();
            }
        },
        {
            text: "Change System Configuration",
            onClick: () => {
                handleEditSystemConfiguration(data);
            }
        },
        {
            text: "Copy Function Configuration",
            onClick: () => {
                setIsDebugMode(true);
                setActiveSystem(data);
                setCopyFunctionConfigurationDialogOpen(true);
            }
        },
        {
            text: "Activate Debug Mode",
            onClick: () => {
                setIsDebugMode(true);
            },
            divider: true
        },
        {
            text: "Delete",
            icon: React.createElement(Delete, { style: { color: Color.danger[500] } }),
            destructive: true,
            onClick: () => {
                setActiveSystem(data);
                setDeleteSystemDialogOpen(true);
            }
        }
    ];
    if (data.deploymentEnv === 1) {
        menuItems.splice(3, 0, {
            text: "Change Device Limit",
            onClick: () => {
                setActiveSystem(data);
                setEditSystemDeviceLimitDialogOpen(true);
            }
        });
    }
    if (data.isDemo) {
        menuItems.splice(1, 1);
    }
    return (React.createElement(React.Fragment, null,
        systemView === "GRID" ? (React.createElement("div", { id: `${id}-container`, style: { position: "relative" } },
            React.createElement("div", { id: id, className: classes.systemBoxContainer, style: {
                    background: data.deploymentEnv === 0 ? Color.neutral[200] : 'transparent',
                    border: data.deploymentEnv === 0 && `4px solid ${Color.neutral[200]}`
                } },
                React.createElement("div", { className: classes.systemBoxHeader },
                    React.createElement(Chip, { id: `${id}-system-deploymentEnv`, backgroundColor: data.deploymentEnv === 0 ? Color.danger[400] : Color.gradient[100], style: { marginRight: 16 }, productionStyle: data.deploymentEnv === 0 ? "test" : "production" }, data.deploymentEnv === 0 ? "TEST" : "PRODUCTION"),
                    data.deploymentEnv === 0 && (React.createElement(Typography, { id: `${id}-system-pin`, fontFamily: FONT_FAMILY.ROBOTO_REGULAR, size: 12, color: Color.primary[400] }, data.pin ? `PIN: ${data.pin}` : ``))),
                React.createElement(Typography, { id: `${id}-system-name`, fontFamily: FONT_FAMILY.CAPITOLIUM_BOLD, size: 30, color: isSystemNameHovered ? Color.primary[500] : Color.neutral[700], style: {
                        marginTop: 26,
                        textDecoration: isSystemNameHovered ? "underline" : "none",
                        cursor: "pointer",
                        transition: "0.3s all ease-in-out"
                    }, onMouseOver: handleSystemNameOnMouseOver, onMouseOut: handleSystemNameOnMouseOut, onClick: onClick }, data.name),
                React.createElement(Typography, { id: `${id}-b1-system-id`, fontFamily: FONT_FAMILY.ROBOTO_REGULAR, size: 12, color: Color.neutral[600] }, data.otgOneId),
                React.createElement("div", { className: classes.systemBoxDatabase },
                    React.createElement("div", { style: { marginTop: 2 } },
                        React.createElement(Tooltip, { title: "Database Name", placement: "right" },
                            React.createElement("div", null,
                                React.createElement(Database, null)))),
                    React.createElement(Typography, { id: `${id}-system-name`, fontFamily: FONT_FAMILY.ROBOTO_REGULAR, size: 14, color: Color.neutral[500], style: { marginLeft: 8 } }, data.dbName ? data.dbName : "-"),
                    React.createElement("div", { style: { marginLeft: 200, marginTop: 2 } },
                        React.createElement(Tooltip, { title: "System ID", placement: "right" },
                            React.createElement("div", null,
                                React.createElement(CPU, null)))),
                    React.createElement(Typography, { id: `${id}-system-id`, fontFamily: FONT_FAMILY.ROBOTO_REGULAR, size: 14, color: Color.neutral[500], style: { marginLeft: 8 } }, data.b1SystemId ? data.b1SystemId : "-")),
                React.createElement(DeviceCount, { data: data, activeDevice: activeDevice, deviceLimit: deviceLimit }),
                React.createElement(ApplicationFunction, { data: data }),
                React.createElement("div", { style: { width: '100%', display: 'flex', justifyContent: 'flex-end' } },
                    React.createElement(Button, { type: "secondary", alignIcon: "right", style: { marginTop: 20, outline: "none" }, onClick: onClick }, `Go to System Dashboard`)),
                React.createElement(ShowMoreHorizontal, { id: `${id}-client-showmore-horiz-icon`, style: {
                        position: "absolute",
                        top: 16,
                        right: 16,
                        width: 24,
                        height: 24,
                        cursor: "pointer",
                        color: Color.primary[400]
                    }, onClick: handleClickShowMoreIcon })))) : (React.createElement(TableRow, { hover: true, onClick: handleClickTableCell, tabIndex: -1, key: data.name, style: {
                height: 85,
                borderBottom: `1px solid ${Color.neutral[400]}`,
                zIndex: 1000,
                cursor: "pointer"
            } },
            React.createElement(TableCell, { align: 'left', style: { fontFamily: FONT_FAMILY.ROBOTO_REGULAR, color: Color.neutral[600] } }, data.name),
            React.createElement(TableCell, { align: 'left', style: { fontFamily: FONT_FAMILY.ROBOTO_REGULAR, color: Color.neutral[600] } },
                React.createElement("div", { style: { display: 'flex', alignItems: 'center' } },
                    React.createElement(Chip, { backgroundColor: data.deploymentEnv === 0 ? Color.danger[400] : Color.gradient[100], style: { marginRight: 10 } }, data.deploymentEnv === 0 ? "TEST" : "PRODUCTION"),
                    data.deploymentEnv === 0 && data.pin)),
            React.createElement(TableCell, { align: 'left', style: { fontFamily: FONT_FAMILY.ROBOTO_REGULAR, color: Color.neutral[600] } }, data.dbName),
            React.createElement(TableCell, { align: 'left', style: { fontFamily: FONT_FAMILY.ROBOTO_REGULAR, color: Color.neutral[600] } }, data.systemName),
            React.createElement(TableCell, { align: 'right', style: { fontFamily: FONT_FAMILY.ROBOTO_REGULAR, color: Color.neutral[600] } }, `${activeDevice} / ${deviceLimit}`),
            React.createElement(TableCell, { align: 'left', style: { fontFamily: FONT_FAMILY.ROBOTO_REGULAR, color: Color.neutral[600] } },
                React.createElement("div", { className: classes.systemApplicationFunctionIconListContainer }, data.functions.map((func, index) => {
                    const isActive = func.isActive === 1;
                    const functionClasses = classnames({
                        [classes.activeFunction]: isActive,
                        [classes.inactiveFunction]: !isActive
                    });
                    return (React.createElement("div", { className: classes.systemApplicationFunction, style: {
                            color: Color.primary[500],
                            marginLeft: index > 0 && 10
                        } },
                        React.createElement(Tooltip, { title: func.name, placement: "right" },
                            React.createElement("div", { className: functionClasses }, getFunctionIcon(func.functionTypeId))),
                        React.createElement(Tooltip, { title: "Customized", placement: "right" },
                            React.createElement("div", { className: classes.systemApplicationFunctionIconStatus, style: {
                                    display: func.isChanged ? "block" : "none",
                                } }))));
                }))),
            React.createElement(TableCell, { align: 'right', onClick: stopClick },
                React.createElement("div", { style: {
                        display: "flex",
                        alignItems: "center",
                        marginTop: -4,
                        zIndex: 100000
                    } },
                    React.createElement(Switch, null),
                    React.createElement(Bug, { style: { width: 24, height: 24, color: Color.neutral[400], marginLeft: 6 } }))),
            React.createElement(TableCell, { align: 'right', style: { position: "relative" } },
                React.createElement("div", { className: classes.moreActionIconContainer },
                    React.createElement(ShowMoreVertical, { style: { color: Color.primary[500] }, onClick: handleClickShowMoreIcon }))))),
        React.createElement(OverflowMenu, { anchorElement: anchorEl, setAnchorEl: setAnchorEl, menuItems: menuItems })));
};
export const System = (props) => {
    const { systemView, data, onClick, setActiveSystem, setDeleteSystemDialogOpen, handleEditSystemConfiguration, setCopyFunctionConfigurationDialogOpen, setEditSystemDeviceLimitDialogOpen } = props;
    return (React.createElement(Grid, { container: true, style: { flexGrow: 1 }, rowSpacing: 4, columnSpacing: 4 }, data.length > 0 ? (React.createElement(React.Fragment, null, systemView === "GRID" ? (React.createElement(React.Fragment, null, data.map((system, index) => (React.createElement(Grid, { item: true, md: 6, key: `system-${index}` },
        React.createElement(SystemView, { systemView: systemView, data: system, onClick: onClick(system), setActiveSystem: setActiveSystem, setDeleteSystemDialogOpen: setDeleteSystemDialogOpen, handleEditSystemConfiguration: handleEditSystemConfiguration, setCopyFunctionConfigurationDialogOpen: setCopyFunctionConfigurationDialogOpen, setEditSystemDeviceLimitDialogOpen: setEditSystemDeviceLimitDialogOpen })))))) : (React.createElement(SystemListHeader, { data: data }, data.map((system, index) => {
        return (React.createElement(SystemView, { systemView: systemView, data: system, onClick: onClick(system), setActiveSystem: setActiveSystem, setDeleteSystemDialogOpen: setDeleteSystemDialogOpen, handleEditSystemConfiguration: handleEditSystemConfiguration, setCopyFunctionConfigurationDialogOpen: setCopyFunctionConfigurationDialogOpen, setEditSystemDeviceLimitDialogOpen: setEditSystemDeviceLimitDialogOpen }));
    }))))) : (React.createElement("div", { style: { width: "100%", textAlign: "center" } },
        React.createElement("div", null,
            React.createElement(EmptySystem, null)),
        React.createElement(Typography, { fontFamily: FONT_FAMILY.ROBOTO_MEDIUM, size: 24, color: Color.neutral[400] }, "No system created for this client.")))));
};
System.defaultProps = {
    data: [{
            deploymentEnv: 0,
            pin: "1234",
            name: "System Name",
            dbName: "North",
            b1SystemId: "North East",
            otgOneId: "AB1238",
            activeDevice: 10,
            deviceLimit: 50,
            defaultDeviceLimit: 25,
            isDemo: false,
            functions: [
                {
                    name: "Goods Receipt",
                    functionTypeId: 1,
                    isActive: 1,
                    isChanged: true
                },
                {
                    name: "Pick & Pack",
                    functionTypeId: 2,
                    isActive: 0,
                    isChanged: false
                },
                {
                    name: "Inventory Status",
                    functionTypeId: 3,
                    isActive: 0,
                    isChanged: false
                },
                {
                    name: "Inventory Transfer",
                    functionTypeId: 4,
                    isActive: 0,
                    isChanged: true
                },
                {
                    name: "Inventory Counting",
                    functionTypeId: 5,
                    isActive: 0,
                    isChanged: false
                },
                {
                    name: "Delivery",
                    functionTypeId: 6,
                    isActive: 0,
                    isChanged: false
                }
            ]
        }],
    onClick: () => { },
    setActiveSystem: null,
    setDeleteSystemDialogOpen: () => { },
    handleEditSystemConfiguration: () => { }
};
export default React.memo(System);
